import { useMutation } from "@apollo/client";
import { Button, Collapse, Drawer, Form, Image, Select, message } from "antd";
import dayjs from "dayjs";
import { capitalize, isEmpty } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../../amplify/authenticator-provider";
import { CREATE_NOTE, UPDATE_NOTE } from "../../../graphql/mutation";
import useCrmIntegration from "../../../hooks/useCrmIntegration";
import { convertUtcToLocal, uploadFilesFromBlobUrls } from "../../../library/utils";
import { getMobileView } from "../../../redux/slices/isMobile";
import RichTextEditorWithAudioRecorder from "./RichTextEditorWithAudioRecorder";
import AudioPlayer from "./audioPlayer";
import { OppId } from "./engagementHelpers";
import { addNote, updateLeadNoteRedux } from "../../../redux/slices/notesSlice";
import {
  getOpportunityDisplayName,
  prepareOpportunityList,
} from "../../../helpers/opportunityHelper";
import {
  getRootOppTypeId,
  getUserAllowedRootOppTypeIds,
  isUserUnscoped,
} from "../../../library/oppTypeScoping";

// Component to render note content with clickable image previews
const NoteContentWithImagePreview = ({ content }) => {
  if (!content) return null;

  // Extract images from HTML content - handles all formats
  const extractImages = htmlContent => {
    const imgRegex = /<img[^>]*\ssrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))[^>]*>/gi;
    const matches = [...htmlContent.matchAll(imgRegex)];
    return matches.map(match => match[1] || match[2] || match[3]).filter(Boolean);
  };

  // Remove images from HTML content for separate rendering
  const removeImages = htmlContent => {
    return htmlContent.replace(/<img[^>]*>/gi, "");
  };

  const images = extractImages(content);
  const contentWithoutImages = removeImages(content);

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: contentWithoutImages }} />
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          <Image.PreviewGroup>
            {images.map((src, index) => (
              <Image
                key={index}
                src={src}
                width={150}
                height={150}
                style={{ objectFit: "cover", cursor: "pointer", borderRadius: 4 }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAK+AOAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAZElEQVR4nO3BMQEAAADCoPVP7W0JoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAeAN1xwABJxXfUgAAAABJRU5ErkJggg=="
                preview={{
                  maskClassName: "rounded",
                }}
                placeholder={
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      borderRadius: 4,
                    }}
                  >
                    Loading...
                  </div>
                }
              />
            ))}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
};

export function NotesDrawer({
  leadNotesData,
  setleadNotesData,
  refetchLeadNotes,
  // Active root opp_type tab — when set, filters the opp dropdown to
  // opps under this root AND auto-selects the most-recent matching
  // opp as the form default in ADD mode.
  selectedOppTypeId,
}) {
  const { mode, leadNote } = leadNotesData;
  const { selectedLead } = useSelector(state => state.lead);
  const dispatch = useDispatch();
  const isMobileView = useSelector(state => getMobileView(state));
  const { user } = useContext(UserContext);
  const [form] = Form.useForm();
  const [formChanged, setformChanged] = useState(false);
  const crmIntegration = useCrmIntegration();
  const [audioUrl, setaudioUrl] = useState([]);
  const [audioBlob, setaudioBlob] = useState(null);
  const [audioLoader, setaudioLoader] = useState(false);
  const [createNote, { loading: createNoteLoader }] = useMutation(CREATE_NOTE);
  const [updateNote, { loading: updateNoteLoader }] = useMutation(UPDATE_NOTE);
  const [notesData, setnotesData] = useState({ noteType: "OPPORTUNITY", note: "" });
  const [isRecording, setIsRecording] = useState(false);
  const stopRecordingRef = useRef(null);
  const close = () => {
    setleadNotesData({ open: false, leadNote: null, mode: null });
    setnotesData({ noteType: "OPPORTUNITY", note: "" });
    setaudioUrl([]);
    setaudioBlob(null);
    form.resetFields();
    setformChanged(false);
  };
  const discardDraft = () => {
    setleadNotesData({ open: false, leadNote: null, mode: null });
    setnotesData({ noteType: "OPPORTUNITY", note: "" });
    setaudioUrl([]);
    setaudioBlob(null);
    form.resetFields();
    setformChanged(false);
  };
  useEffect(() => {
    discardDraft();
  }, [selectedLead.id]);
  useEffect(() => {
    if (leadNote?.voiceNotes?.length > 0) {
      setaudioUrl(leadNote?.voiceNotes?.map(item => item?.voiceNote));
    }
  }, [leadNote?.voiceNotes]);

  useEffect(() => {
    if (mode === "EDIT" && leadNote) {
      setnotesData({
        noteType: leadNote.noteType || "OPPORTUNITY",
        note: leadNote.note || "",
        ...leadNote,
      });
    } else if (mode === "ADD") {
      setnotesData({ noteType: "OPPORTUNITY", note: "" });
      setaudioUrl([]);
      form.resetFields();
    }
  }, [leadNote, mode]);
  // Lead's opps filtered to (a) the user's permitted roots and (b) the
  // active tab's root (when one is set). Memoized so identity-stable
  // refs don't churn the form's Select between renders.
  const scopedOpps = (() => {
    const allOpps = selectedLead?.leadOpportunities || [];
    const userAllowed = getUserAllowedRootOppTypeIds(user);
    const unscoped = isUserUnscoped(user);
    const permittedOpps = allOpps.filter(opp => {
      if (unscoped) return true;
      const rootId = getRootOppTypeId(opp?.opportunityType);
      // Opp-less or unknown root: surface — better than hiding.
      if (!rootId) return true;
      return userAllowed.has(rootId);
    });
    if (!selectedOppTypeId) return permittedOpps;
    const tabScoped = permittedOpps.filter(
      opp => Number(getRootOppTypeId(opp?.opportunityType)) === Number(selectedOppTypeId)
    );
    // If the active tab has no matching permitted opp, fall back to the
    // wider permitted set so the user can still pick something rather
    // than seeing an empty dropdown.
    return tabScoped.length > 0 ? tabScoped : permittedOpps;
  })();
  const oppoDropdown = prepareOpportunityList(scopedOpps, crmIntegration) || [];

  // Set the default opportunity (latest one within the user-permitted,
  // active-tab-scoped set) ONCE when the drawer opens in ADD mode.
  // Picking from ``scopedOpps`` rather than the raw lead opp list
  // ensures we don't auto-select an opp the user can't act on.
  useEffect(() => {
    if (mode !== "ADD" || !leadNotesData.open) return;
    if (!scopedOpps || scopedOpps.length === 0) return;
    const latestOpportunity = scopedOpps.reduce((latest, current) =>
      dayjs(current.updatedDate).isAfter(dayjs(latest.updatedDate)) ? current : latest
    );
    if (latestOpportunity) {
      form.setFieldValue("leadOpportunityId", latestOpportunity.id);
    }
  }, [mode, leadNotesData.open, selectedLead?.id, selectedOppTypeId]);
  useEffect(() => {
    if (mode === "EDIT" && leadNote) {
      form.setFieldsValue({
        noteType: leadNote.noteType || "OPPORTUNITY",
        note: leadNote.note || "",
        // Coerce to number so it matches the Select option values (value: +item.id),
        // otherwise the string id won't match and the Select shows the raw id.
        leadOpportunityId: leadNote?.leadOpportunity?.id ? +leadNote.leadOpportunity.id : null,
      });
    } else if (mode === "ADD") {
      form.setFieldsValue({ noteType: "OPPORTUNITY", note: "" });
    }
  }, [leadNote, mode]);
  const handleSave = async values => {
    // Stop recording if it's ongoing
    if (isRecording && stopRecordingRef.current) {
      // Wait for the recording to stop and audio URL to be added
      await stopRecordingRef.current();
    }

    // Validate note field - check if it's empty or contains only HTML tags/whitespace
    const noteText = notesData.note?.replace(/<[^>]*>/g, "").trim();
    if (!noteText && audioUrl.length === 0) {
      message.error("Please add a note or voice recording before saving");
      return;
    }

    const payload = {
      ...values,
      leadId: selectedLead.id,
      userId: user.id,
    };
    const urls = await uploadFilesFromBlobUrls(
      audioUrl.filter(item => item).filter(item => item.includes("localhost")),
      `${selectedLead?.fullName || "N/A"}-${dayjs().toISOString()}`,
      user.id,
      selectedLead.id,
      setaudioLoader
    );
    if (mode === "ADD") {
      const res = await createNote({
        variables: {
          ...payload,
          voiceNotes: [
            ...urls,
            ...audioUrl.filter(item => item).filter(item => !item.includes("localhost")),
          ].map(item => ({ voiceNote: item })),
        },
      });
      if (res.data.createNote.statusCode === 200) {
        message.success(res.data.createNote.message);
        dispatch(addNote(res.data.createNote.leadNote));
        close();
      } else {
        message.error(res.data.createNote.message);
      }
    } else {
      const res = await updateNote({
        variables: {
          id: leadNote.id,
          ...payload,
          voiceNotes: [
            ...urls,
            ...audioUrl.filter(item => item).filter(item => !item.includes("localhost")),
          ].map(item => ({ voiceNote: item })),
        },
      });
      if (res.data.updateNote.statusCode === 200) {
        message.success(res.data.updateNote.message);

        dispatch(updateLeadNoteRedux(res.data.updateNote.leadNote));
        close();
      } else {
        message.error(res.data.updateNote.message);
      }
    }
  };
  useEffect(() => {
    form.setFieldsValue(notesData);
  }, [notesData]);
  return (
    <Drawer
      open={leadNotesData.open}
      className="normal-popup"
      width={isMobileView ? "100%" : "40%"}
      title={`${capitalize(mode)} Note for ${
        leadNote?.leadOpportunity?.name || leadNotesData?.selectedLead?.fullName || ""
      }`}
      onClose={close}
      footer={
        mode !== "VIEW" ? (
          <div className="flex-row-centered gap-2 items-center">
            <Button
              loading={createNoteLoader || updateNoteLoader || audioLoader}
              type="primary"
              onClick={() => form.submit()}
            >
              Save
            </Button>
            <Button onClick={close}>Cancel</Button>
            {formChanged && (
              <Button type="link" onClick={discardDraft}>
                Discard draft
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <div className="h-full w-full overflow-y-auto no-scroll">
        <div>
          {mode !== "VIEW" ? (
            <Form
              onFinish={values => handleSave({ ...values, ...notesData })}
              layout="vertical"
              onFieldsChange={() => setformChanged(true)}
              form={form}
            >
              <div className="flex-row-centered gap-2 items-center justify-between">
                <Form.Item name="noteType" label="Note Type" className="w-full">
                  <Select
                    name="noteType"
                    options={[
                      { label: "Customer Note", value: "LEAD" },
                      { label: "Opportunity Note", value: "OPPORTUNITY" },
                    ]}
                    disabled={mode === "EDIT"}
                    placeholder="Note Type"
                    onSelect={value => setnotesData({ ...notesData, noteType: value })}
                  />
                </Form.Item>
                {notesData.noteType === "OPPORTUNITY" && (
                  <Form.Item
                    rules={[{ required: true, message: "Please select Opportunity" }]}
                    name="leadOpportunityId"
                    className="w-full"
                    label="Select Opportunity"
                  >
                    <Select
                      options={oppoDropdown.map(item => ({
                        label: getOpportunityDisplayName(item),
                        value: +item.id,
                      }))}
                      disabled={mode === "EDIT"}
                      placeholder="Select Opportunity"
                      name="leadOpportunityId"
                    />
                  </Form.Item>
                )}
              </div>
              {audioUrl.length > 0 && (
                <Collapse className="transparent-collapse w-full rounded-none">
                  <Collapse.Panel
                    header={<span className="text-xs font-[600] text-grey-700">Recordings</span>}
                    className="lead-profile-sections"
                  >
                    {audioUrl.map((url, index) => (
                      <AudioPlayer
                        key={index}
                        showClose
                        onClose={() =>
                          setaudioUrl(prev => {
                            return prev.slice(0, index);
                          })
                        }
                        src={url}
                      />
                    ))}
                  </Collapse.Panel>
                </Collapse>
              )}
              <Form.Item
                // rules={[{ required: true, message: "Lead Note is required" }]}
                name="note"
                label="Note"
                className="relative w-full h-[57vh]"
              >
                <RichTextEditorWithAudioRecorder
                  content={notesData.note}
                  setcontent={value => {
                    setformChanged(true);
                    setnotesData({ ...notesData, note: value });
                  }}
                  leadNote={leadNote}
                  audioUrl={audioUrl}
                  setAudioUrl={setaudioUrl}
                  audioBlob={audioBlob}
                  setaudioBlob={setaudioBlob}
                  mode={mode}
                  showColorPicker={false}
                  isRecording={isRecording}
                  setIsRecording={setIsRecording}
                  stopRecordingRef={stopRecordingRef}
                />
              </Form.Item>
            </Form>
          ) : (
            <div className="flex-col-centered items-start gap-2">
              <div className="flex-col-centered items-start gap-1">
                <span>Note</span>
              </div>
              <div className="w-full border-gray-200 border-solid border-[2px] p-2 pl-[25px] h-[70vh] overflow-y-auto break-words overflow-x-hidden">
                <NoteContentWithImagePreview content={leadNote.note} />
              </div>
              <span className="text-xs ">
                By: {leadNote?.updatedBy?.fullName || leadNote?.createdBy?.fullName} -{" "}
                {convertUtcToLocal(
                  leadNote?.updateTs || leadNote?.createTs,
                  "MM/DD/YYYY hh:mm:ss A"
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
